"use client";

import { Button } from "@/components/ui/Button";

export function EmptyState({ 
  illustration, 
  title, 
  description, 
  actionText, 
  actionHref,
  actionOnClick,
  className = ""
}) {
  const handleClick = () => {
    if (actionOnClick) {
      actionOnClick();
    } else if (actionHref) {
      window.location.href = actionHref;
    }
  };

  return (
    <div className={`empty-state ${className}`} style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "3rem 2rem",
      minHeight: "400px",
      gap: "1.5rem"
    }}>
      {illustration && (
        <div style={{
          width: "120px",
          height: "120px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: 0.8
        }}>
          {illustration}
        </div>
      )}
      
      <div style={{ maxWidth: "400px" }}>
        <h3 style={{
          fontSize: "1.25rem",
          fontWeight: 600,
          color: "#0f172a",
          margin: "0 0 0.75rem 0",
          letterSpacing: "-0.02em"
        }}>
          {title}
        </h3>
        
        <p style={{
          fontSize: "0.9375rem",
          color: "#475569",
          margin: "0 0 1.5rem 0",
          lineHeight: 1.6
        }}>
          {description}
        </p>
        
        {(actionText || actionHref || actionOnClick) && (
          <Button
            onClick={handleClick}
            variant="primary"
            size="lg"
            style={{ minWidth: "160px" }}
          >
            {actionText}
          </Button>
        )}
      </div>
    </div>
  );
}

// Predefined illustrations as SVG components
export const EmptyStateIllustrations = {
  // Dashboard empty state - no medications
  EmptyInventory: (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="2"/>
      <rect x="35" y="35" width="50" height="40" rx="4" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2"/>
      <rect x="40" y="40" width="40" height="2" rx="1" fill="#94a3b8"/>
      <rect x="40" y="45" width="30" height="2" rx="1" fill="#cbd5e1"/>
      <rect x="40" y="50" width="35" height="2" rx="1" fill="#cbd5e1"/>
      <circle cx="60" cy="75" r="8" fill="#3b82f6" opacity="0.2"/>
      <path d="M57 75 L60 78 L63 75" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  // History empty state - no scans
  NoHistory: (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="2"/>
      <rect x="30" y="30" width="60" height="50" rx="4" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2"/>
      <rect x="40" y="40" width="40" height="3" rx="1" fill="#94a3b8"/>
      <rect x="40" y="47" width="30" height="3" rx="1" fill="#cbd5e1"/>
      <rect x="40" y="54" width="35" height="3" rx="1" fill="#cbd5e1"/>
      <rect x="40" y="61" width="25" height="3" rx="1" fill="#cbd5e1"/>
      <circle cx="60" cy="75" r="8" fill="#10b981" opacity="0.2"/>
      <path d="M55 75 L60 80 L65 75" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  // Search empty state - no results
  NoResults: (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="2"/>
      <circle cx="48" cy="48" r="18" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2"/>
      <rect x="60" y="60" width="4" height="20" rx="2" fill="#cbd5e1" transform="rotate(45 62 70)"/>
      <circle cx="48" cy="48" r="12" fill="none" stroke="#94a3b8" strokeWidth="2"/>
      <circle cx="60" cy="75" r="8" fill="#f59e0b" opacity="0.2"/>
      <path d="M57 75 L60 78 L63 75" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  // Error state
  Error: (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="#fef2f2" stroke="#fecaca" strokeWidth="2"/>
      <circle cx="60" cy="60" r="25" fill="#ffffff" stroke="#ef4444" strokeWidth="2"/>
      <path d="M52 52 L68 68 M68 52 L52 68" stroke="#ef4444" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),

  // Loading state
  Loading: (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2"/>
      <circle cx="60" cy="60" r="20" fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.4 31.4">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 60 60"
          to="360 60 60"
          dur="1s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  )
};
